<template>
  <component
    :is="closeable ? 'div' : 'button'"
    class="search-data"
    :class="{ 'search-data--clickable': !closeable }"
  >
    <button
      v-if="closeable"
      class="search-data__remove-btn"
      @click.stop="$emit('on-close')"
    />
    <span class="search-data__label">
      {{ title }}
    </span>
    <template v-if="type === 'string'">
      <span class="search-data__info">
        {{ option }}:
        <span class="search-data__info--highlight">{{ value }}</span>
      </span>
    </template>
    <template v-else-if="type === 'list'">
      <span class="search-data__info">
        {{ option }}: <span
          v-for="(val, idx) in value"
          :key="`${val}-${idx}`"
        >
          <span
            class="search-data__info--highlight"
          >{{ val }}</span>
          <span
            v-if="idx !== value.length-1"
          >, </span>
        </span>
      </span>
    </template>
    <template v-else-if="type === 'user-field'">
      <span
        v-if="value.userProfile"
        class="search-data__info"
      >
        {{ option.userProfile }}:
        <span class="search-data__info--highlight">{{ value.userProfile }}</span>
      </span>
      <span
        v-if="value.name"
        class="search-data__info"
      >
        {{ option }}:
        <span class="search-data__info--highlight">{{ value.name }}</span>
      </span>
      <span
        v-if="value.affiliations"
        class="search-data__info"
      >
        {{ option.affiliation }}: <span
          v-for="(val, idx) in value.affiliations"
          :key="`${val}-${idx}`"
        >
          <span
            class="search-data__info--highlight"
          >{{ val }}</span>
          <span
            v-if="idx !== value.affiliations.length-1"
          >, </span>
        </span>
      </span>
    </template>
    <template v-else-if="type === 'date-field'">
      <span class="search-data__info">
        {{ option }}:
        <span class="search-data__info--highlight">{{ generateDateString(value.start) }}</span>
        <span v-if="value.end"> - </span>
        <span
          v-if="value.end"
          class="search-data__info--highlight"
        >{{ generateDateString(value.end) }}</span>
      </span>
    </template>
    <template v-else-if="type === 'boolean-field'">
      <span class="search-data__info">
        <span class="search-data__info--highlight">{{ $t(`advancedsearch.fields.boolean.${value}`) }}</span>
      </span>
    </template>
  </component>
</template>

<script>
import RecordSearchFieldsHelpers from '#src/mixins/RecordSearchFieldsHelpers'
import DateHelpers from '#src/mixins/DateHelpers'

export default {
  mixins: [RecordSearchFieldsHelpers, DateHelpers],
  props: {
    field: {
      type: Object,
      required: true
    },
    closeable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['on-close'],
  data() {
    return {
      type: null
    }
  },
  computed: {
    title: function () {
      const fieldName = this.getFieldLabelName(this.field.name)
      return this.$t(`fields.record.${fieldName}.label`)
    },
    value: function () {
      if (this.type === 'string') {
        return this.field.value.value
      }
      const rawType = this.getFieldType(this.field.name)
      if (rawType === 'enum-field') {
        const fieldName = this.getFieldLabelName(this.field.name)
        return this.field.value.value
          .map(v => this.$t(`fields.record.${fieldName}.values.${v}`))
      }
      if (this.type === 'list') {
        return this.field.value.value
      }
      if (this.type === 'date-field') {
        const value = { start: this.field.value.value.start }
        if (this.field.value.option === 'between') {
          value.end = this.field.value.value.end
        }
        return value
      }
      if (this.type === 'user-field') {
        return {
          userProfile: this.field.value.display.userProfile,
          affiliations: this.field.value.display.affiliations,
          name: this.field.value.display.name
        }
      }
      return this.field.value.value
    },
    option: function () {
      if (this.type === 'string') {
        return this.$t(`advancedsearch.fields.string.options.${this.field.value.option}`)
      }
      if (this.type === 'list') {
        return this.$t(`advancedsearch.fields.enum.options.${this.field.value.option}`)
      }
      if (this.type === 'date-field') {
        return this.$t(`advancedsearch.fields.date.options.${this.field.value.option}`)
      }
      if (this.type === 'user-field') {
        if (['all', 'any', 'none'].includes(this.field.value.option)) {
          const name = this.$t('advancedsearch.fields.user.options.name')
          const type = this.$t(`advancedsearch.fields.string.options.${this.field.value.option}`)
          return `${name} (${type})`
        } else {
          return {
            userProfile: this.$t('advancedsearch.fields.user.options.userId'),
            affiliation: this.$t('advancedsearch.fields.user.options.affiliation')
          }
        }
      }
      return this.field.value.option
    }
  },
  created() {
    const type = this.getFieldType(this.field.name)
    if ([
      'string-field',
      'text-area-field'
    ].includes(type)) {
      this.type = 'string'
    } else if ([
      'keyword-field',
      'subject-field',
      'number-field',
      'enum-field'
    ].includes(type)) {
      this.type = 'list'
    } else {
      // date-field, user, and boolean field
      this.type = type
    }
  }
}

</script>

<style lang="scss" scoped>
@use "sass:color";
@use "#src/assets/scss/variables" as *;

span {
  color: $slate;
}

.search-data {
  width: 100%;
  user-select: none;
  position: relative;
  background: white;
  box-shadow: 0 2px 4px 0 color.change(black, $alpha: 0.1);
  border-radius: 2px;
  padding: 0.5rem 1rem;

  .search-data__label {
    font-weight: 600;
    display: block;
    margin-bottom: 0.25rem;
  }

  &--clickable {
    cursor: pointer;
  }
}

.search-data__info {
  display: block;
  font-size: 0.8125rem;
  color: $gray-400;

  &--highlight {
    color: $violet;
  }
}

.search-data__remove-btn {
  position: absolute;
  top: -0.4rem;
  right: -0.4rem;
  width: 1.25rem;
  height: 1.25rem;
  overflow: hidden;
  background-color: white;
  border-radius: 100%;
  text-indent: 100%;
  border: 1px solid $light-blue;

  &::before {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='7' height='7' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.7071.2929L3.121 1.706 4.53553.2929c.39053-.39053 1.0237-.39053 1.41422 0 .39052.39052.39052 1.02368 0 1.4142L4.535 3.121l1.41475 1.41453c.39052.39053.39052 1.0237 0 1.41422-.39053.39052-1.0237.39052-1.41422 0L3.121 4.535 1.7071 5.94975c-.39052.39052-1.02368.39052-1.4142 0-.39053-.39053-.39053-1.0237 0-1.41422L1.706 3.121.2929 1.7071c-.39053-.39052-.39053-1.02368 0-1.4142.39052-.39053 1.02368-.39053 1.4142 0z' fill='%238F95A1' fill-rule='evenodd'/%3E%3C/svg%3E");
    background-size: 0.5rem 0.5rem;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    background-color: white;
  }
}
</style>
